/**
 * 编辑资料(昵称 / 简介)。消费 GET/POST /api/v1/users/me/profile(后端已有,零改动)。
 * 用途:① 个人中心「编辑资料」入口;② 发布守卫链要求昵称时的落地页(from=/publish 回跳)。
 * 头像上传后续接(avatar-upload-credential 预签名),本期先做昵称+简介文本。
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getMyProfile, updateProfile } from '@/api/profile';
import { ServerError } from '@/api/client';
import { queryClient } from '@/lib/queryClient';
import { toast } from '@/components/ui/toast';

const NICK_MAX = 20;
const BIO_MAX = 60;

interface FromState {
  from?: string;
}

export function EditProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as FromState | null)?.from;

  const profileQuery = useQuery({ queryKey: ['me', 'profile'], queryFn: getMyProfile, retry: false });

  const [nickname, setNickname] = useState('');
  const [bio, setBio] = useState('');
  const [prefilled, setPrefilled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitLock = useRef(false);

  // 首次载入回填(仅一次,避免覆盖用户编辑)
  useEffect(() => {
    if (prefilled || !profileQuery.data) return;
    setNickname(profileQuery.data.nickname?.trim() ?? '');
    setBio(profileQuery.data.bio?.trim() ?? '');
    setPrefilled(true);
  }, [profileQuery.data, prefilled]);

  const nick = nickname.trim();
  const nickOk = [...nick].length > 0 && [...nick].length <= NICK_MAX;
  const bioOk = [...bio].length <= BIO_MAX;
  const canSave = nickOk && bioOk && !submitting;

  const onSave = async () => {
    setError(null);
    if (!nickOk) {
      setError(`请填写昵称(1-${NICK_MAX} 字)`);
      return;
    }
    if (!bioOk) {
      setError(`简介最多 ${BIO_MAX} 字`);
      return;
    }
    if (submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    try {
      await updateProfile({ nickname: nick, bio: bio.trim() });
      // 两处 profile 缓存都刷新(个人中心用 ['me','profile'],发布页用 ['myProfile'])
      await queryClient.invalidateQueries({ queryKey: ['me', 'profile'] });
      await queryClient.invalidateQueries({ queryKey: ['myProfile'] });
      toast.success('资料已保存');
      navigate(from ?? '/me', { replace: true });
    } catch (err) {
      setError(err instanceof ServerError ? err.message || '保存失败,请稍后重试' : '保存失败,请稍后重试');
    } finally {
      setSubmitting(false);
      submitLock.current = false;
    }
  };

  return (
    <div className="inner section">
      <p className="sm muted mb-6">首页 / 个人中心 / 编辑资料</p>
      <div className="row between mb-8">
        <h1 className="h2">编辑资料</h1>
        <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
          取消
        </button>
      </div>

      <div className="card card-pad" style={{ maxWidth: 560 }}>
        {from === '/publish' && (
          <p className="sm mb-4" style={{ color: 'var(--color-primary)' }}>
            发布商品前请先设置昵称。
          </p>
        )}

        <div className="field">
          <label className="label" htmlFor="pf-nick">
            昵称
          </label>
          <input
            id="pf-nick"
            className="input"
            type="text"
            placeholder="给自己起个昵称"
            maxLength={NICK_MAX}
            value={nickname}
            onChange={(e) => {
              setError(null);
              setNickname(e.target.value);
            }}
          />
          <p className="hint">
            {[...nick].length}/{NICK_MAX}
          </p>
        </div>

        <div className="field">
          <label className="label" htmlFor="pf-bio">
            个人简介
          </label>
          <textarea
            id="pf-bio"
            className="textarea"
            style={{ minHeight: 100 }}
            placeholder="一句话介绍自己(选填)"
            maxLength={BIO_MAX}
            value={bio}
            onChange={(e) => {
              setError(null);
              setBio(e.target.value);
            }}
          />
          <p className="hint">
            {[...bio].length}/{BIO_MAX}
          </p>
        </div>

        {error && (
          <p className="sm mt-1" role="alert" style={{ color: 'var(--color-destructive)' }}>
            {error}
          </p>
        )}

        <button
          type="button"
          className="btn btn-primary btn-lg btn-block mt-6"
          disabled={!canSave}
          onClick={onSave}
        >
          {submitting ? '保存中…' : '保存'}
        </button>
      </div>
    </div>
  );
}
